import Title from "common/Typography/Title";
import Text from "common/Typography/Text";
import styled, { useTheme } from "styled-components";
import React, { useState } from "react";
import { formatDate } from "@shared/utils/date";
import Rate from "common/Rate/Rate";
import Card from "common/Boxes/Card";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import ColumnTitle from "common/Typography/ColumnTitle";
import { SortingOrder } from "types";
import { SubGroupReview, LanguageNameSpaces } from "@shared/types";
import EmptyView from "common/Empty/EmptyView";
import { EmptyStatesType } from "common/Icons/types.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import NewLookReviewsList from "features/newMarketplace/GroupExtraDetails/Tabs/ReviewsTab/NewLookReviewsList";
import GroupsListMessage from "features/newMarketplace/NewLook/GroupsListMessage.tsx";
import Panel from "common/NewLook/Panel.tsx";

interface ReviewsListProps {
  subGroupReviews: SubGroupReview[];
  reviewsLoaded: boolean;
}

const ReviewsList = ({ subGroupReviews, reviewsLoaded }: ReviewsListProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { isNewLook } = useTheme();

  const reviewsList = subGroupReviews.filter(
    (review) => !review.current_user_comment
  );
  const noReviews = reviewsLoaded && reviewsList?.length === 0;

  const [sortOrder, setSortOrder] = useState(SortingOrder.DESCEND);
  const [sortingBy, setSortingBy] = useState("Date");

  const sortByDate = (reviewsList: SubGroupReview[]) => {
    return reviewsList
      .slice()
      .sort(
        (a, b) => sortOrder * (a.creation_timestamp - b.creation_timestamp)
      );
  };

  const sortByRating = (reviewsList: SubGroupReview[]) => {
    return reviewsList.slice().sort((a, b) => sortOrder * (a.stars - b.stars));
  };

  const getSortedReviews = () => {
    if (sortingBy === "Rating") {
      return sortByRating(reviewsList);
    }
    return sortByDate(reviewsList);
  };

  const setOppositeOrder = () => {
    if (sortOrder === SortingOrder.DESCEND) setSortOrder(SortingOrder.ASCEND);

    if (sortOrder === SortingOrder.ASCEND) setSortOrder(SortingOrder.DESCEND);
  };

  const changeSort = (clickedColumn: string) => {
    if (clickedColumn != sortingBy) {
      setSortOrder(SortingOrder.DESCEND);
      setSortingBy(clickedColumn);
    } else setOppositeOrder();
  };

  if (noReviews && isNewLook) {
    return (
      <Panel title={t("drawer.tabs.reviews")} flush={true}>
        <GroupsListMessage title={t("reviews.no_reviews_yet")} />
      </Panel>
    );
  }

  if (noReviews) {
    return (
      <NoReviewsWrapper>
        <EmptyView
          iconKey={EmptyStatesType.Reviews}
          iconWidth={isMobile ? 100 : 120}
          title={t("reviews.no_reviews_yet")}
        />
      </NoReviewsWrapper>
    );
  }

  if (isNewLook) {
    return (
      <NewLookReviewsList
        reviews={getSortedReviews()}
        sortingBy={sortingBy}
        sortOrder={sortOrder}
        setSortingBy={(value) => {
          setSortOrder(SortingOrder.DESCEND);
          setSortingBy(value);
        }}
        setSortOrder={setSortOrder}
      />
    );
  }

  return (
    <>
      <HeadersCard style={{ padding: isMobile ? "12px 18px" : "12px 24px" }}>
        <ColumnTitle
          columnName={t("reviews.date")}
          sortingBy={sortingBy}
          sortOrder={sortOrder}
          onClick={() => changeSort("Date")}
          titleSize={4}
        />
        <ColumnTitle
          columnName={t("reviews.rating_column")}
          sortingBy={sortingBy}
          sortOrder={sortOrder}
          onClick={() => changeSort("Rating")}
          titleSize={4}
        />
      </HeadersCard>
      {getSortedReviews().map((review, index) => (
        <ReviewCard key={index} style={{ padding: isMobile ? 18 : 24 }}>
          <Wrapper>
            <div>
              <UserName level={3}>{review.full_name}</UserName>
              <ReviewCreationDate level={4}>
                {formatDate(review.creation_timestamp, false, true)}
              </ReviewCreationDate>
            </div>
            <Rate
              disabled
              value={review.stars}
              style={{ fontSize: 16, top: 4, position: "relative" }}
            />
          </Wrapper>
          <Text type="secondary">{review.message}</Text>
        </ReviewCard>
      ))}
    </>
  );
};

export default ReviewsList;

const NoReviewsWrapper = styled.div`
  display: flex;
  justify-content: center;
`;
const ReviewCard = styled(Card)`
  margin-bottom: 16px;
  background: ${(p) => p.theme.colors.cardBgColor};
  ${(p) =>
    p.theme.isDarkMode &&
    `
    box-shadow: none;
  `}
`;
const HeadersCard = styled(Card)`
  background: ${(p) => p.theme.colors.titleCardBgColor};
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
  box-shadow: none;
`;
const ReviewCreationDate = styled(Title)`
  color: ${(p) => p.theme.colors.secondaryText};
  margin-bottom: 16px;
`;
const UserName = styled(Title)`
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
`;
const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;
